import dayjs from "dayjs";
import pluralize from "pluralize";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearchParam } from "src/hooks";
import { EmptyState } from "src/components";
import {
	HashtagFilterBar,
	SocialPostContentFilter
} from "src/features/SocialFeedContent/components";
import { SocialPostList } from "src/features/SocialFeedContent/containers";
import { useSocialPosts } from "src/features/SocialFeedContent/hooks";
import { useDebounce } from "react-use";

const SocialFeedContent = () => {
	const DEFAULT_URL_PARAMS = {
		search: "",
		hashtag: "",
		author: "",
		startDate: dayjs().subtract(3, "month").format("YYYY-MM-DD"),
		endDate: dayjs().format("YYYY-MM-DD")
	};

	const [, setSearchParams] = useSearchParams();

	const [search, setSearch] = useSearchParam({
		key: "search",
		defaultValue: ""
	});

	const [hashtag, setHashtag] = useSearchParam({
		key: "hashtag",
		defaultValue: ""
	});

	const [author, setAuthor] = useSearchParam({
		key: "author",
		defaultValue: ""
	});

	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [debouncedHashtag, setDebouncedHashtag] = useState("");

	const [startDate, setStartDate] = useSearchParam({
		key: "startDate",
		defaultValue: DEFAULT_URL_PARAMS.startDate
	});

	const [endDate, setEndDate] = useSearchParam({
		key: "endDate",
		defaultValue: DEFAULT_URL_PARAMS.endDate
	});

	const {
		data: posts,
		isLoading: isLoadingPosts,
		isFetching: isFetchingPosts,
		isFetchingNextPage: isFetchingNextPagePosts,
		hasNextPage: hasNextPagePosts,
		fetchNextPage: fetchNextPagePosts
	} = useSocialPosts({
		search: debouncedSearch,
		hashtag: debouncedHashtag,
		author,
		startDate,
		endDate
	});

	useDebounce(() => setDebouncedSearch(search), 300, [search]);
	useDebounce(() => setDebouncedHashtag(hashtag), 300, [hashtag]);

	const handleOnEndReached = () => {
		if (hasNextPagePosts) {
			fetchNextPagePosts();
		}
	};

	const handleResetFiltersClick = () => {
		setSearchParams(DEFAULT_URL_PARAMS);
		setDebouncedSearch("");
		setDebouncedHashtag("");
	};

	const handleStartDateChange = (date: Date) => {
		setStartDate(dayjs(date).format("YYYY-MM-DD"));
	};

	const handleEndDateChange = (date: Date) => {
		setEndDate(dayjs(date).format("YYYY-MM-DD"));
	};

	const renderResultCount = () => {
		const count = posts?.pages[0]?.count;

		if (isLoadingPosts || isFetchingPosts) {
			return "Loading posts...";
		}

		if (!count || count === 0) {
			return "Showing 0 results";
		}

		return `Showing ${count} ${pluralize("result", count)}`;
	};

	const socialPosts = posts?.pages.flatMap(({ results }) => results);

	const isResetFiltersBtnDisabled =
		search === "" &&
		hashtag === "" &&
		author === "" &&
		startDate === DEFAULT_URL_PARAMS.startDate &&
		endDate === DEFAULT_URL_PARAMS.endDate;

	return (
		<div>
			<SocialPostContentFilter
				search={search}
				hashtag={hashtag}
				debouncedHashtag={debouncedHashtag}
				author={author}
				startDate={dayjs(startDate).toDate()}
				endDate={dayjs(endDate).toDate()}
				isResetFiltersBtnDisabled={isResetFiltersBtnDisabled}
				onSearchChange={setSearch}
				onHashtagChange={setHashtag}
				onAuthorChange={setAuthor}
				onStartDateChange={handleStartDateChange}
				onEndDateChange={handleEndDateChange}
				onResetFiltersClick={handleResetFiltersClick}
			/>

			<div className="max-w-2xl mx-auto">
				<div className="mt-4">
					<HashtagFilterBar
						selectedHashtag={hashtag}
						onHashtagChange={setHashtag}
					/>
				</div>

				<p
					className="text-sm font-medium text-zinc-600 mt-6 mb-4"
					aria-live="polite"
				>
					{renderResultCount()}
				</p>

				{(isLoadingPosts || isFetchingPosts) && !socialPosts?.length && (
					<div className="flex flex-col gap-4">
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className="rounded-xl border border-border bg-white shadow-sm overflow-hidden animate-pulse"
							>
								<div className="px-4 py-3 flex items-center gap-3">
									<div className="w-9 h-9 rounded-full bg-zinc-200" />
									<div className="flex-1">
										<div className="h-3.5 w-32 bg-zinc-200 rounded mb-1.5" />
										<div className="h-3 w-20 bg-zinc-100 rounded" />
									</div>
									<div className="h-3 w-16 bg-zinc-100 rounded" />
								</div>
								<div className="px-4 pb-3 space-y-2">
									<div className="h-3 w-full bg-zinc-100 rounded" />
									<div className="h-3 w-3/4 bg-zinc-100 rounded" />
								</div>
								<div className="mx-4 mb-3 h-48 bg-zinc-100 rounded-xl" />
								<div className="px-4 py-2.5 border-t border-border flex justify-between">
									<div className="h-3 w-20 bg-zinc-100 rounded" />
									<div className="h-3 w-24 bg-zinc-100 rounded" />
								</div>
							</div>
						))}
					</div>
				)}

				{socialPosts?.length === 0 && !isLoadingPosts && !isFetchingPosts && (
					<EmptyState
						title="No content found"
						description="Reset filters and try again."
					/>
				)}

				{socialPosts && socialPosts?.length > 0 && (
					<SocialPostList
						posts={socialPosts}
						onEndReached={handleOnEndReached}
						isFetchingNextPage={isFetchingNextPagePosts}
						onHashtagClick={setHashtag}
					/>
				)}
			</div>
		</div>
	);
};

export default SocialFeedContent;
